/**
 * Turns the raw rows of a Google Sheet into something the components can use.
 *
 * Sheet columns (unchanged from the old site):
 *   A Content | B Background colour | C Extra | D Type | E Mobile/Desktop | F Description
 */

import { SITE } from '../config';

const KNOWN_TYPES = new Set([
  'title', 'subtitle', 'text', 'simpletext', 'link', 'presslink', 'youtube',
  'image', 'imagelink', 'imageintext', 'images', 'imagearray', 'textwithimage',
  'soundcloud', 'iframe', 'hr', 'space',
]);

const escapeRe = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const OLD_EMAILS = (SITE.oldEmails || []).map((e) => new RegExp(escapeRe(e), 'gi'));
const REPLACEMENTS = (SITE.replacements || []).map(([from, to]) => [new RegExp(escapeRe(from), 'gi'), to]);
export const clean = (v) => {
  let out = (v == null ? '' : String(v)).replace(/\u00a0/g, ' ').trim();
  OLD_EMAILS.forEach((re) => { out = out.replace(re, SITE.email); });
  REPLACEMENTS.forEach(([re, to]) => { out = out.replace(re, to); });
  return out;
};
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
const splitFirst = (str, sep) => {
  const i = str.indexOf(sep);
  return i === -1 ? [str, ''] : [str.slice(0, i), str.slice(i + 1)];
};

// ---------------------------------------------------------------------------
// Small text helpers
// ---------------------------------------------------------------------------

const SMALL_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'with']);

/** "ABOUT US" -> "About Us". Text that is not shouting is left exactly as typed. */
export function prettifyTitle(str) {
  if (!str) return '';
  const letters = str.replace(/[^A-Za-z]/g, '');
  const shouting = letters.length > 4 && str === str.toUpperCase();
  if (!shouting) return str;
  return str
    .toLowerCase()
    .split(/(\s+)/)
    .map((w, i) => {
      if (/^\s+$/.test(w) || !w) return w;
      if (i > 0 && SMALL_WORDS.has(w.replace(/[^a-z]/g, ''))) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join('');
}

export function ytId(str) {
  const s = clean(str);
  const m = s.match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : s.replace(/[^\w-]/g, '');
}

/** "Text $ Second paragraph $ Label LINK https://..." -> [{text}|{label,url}] */
export function parseDesc(desc) {
  if (!desc) return [];
  return desc.split(/\s\$\s/).map((part) => {
    const p = part.trim();
    if (p.includes(' LINK ')) {
      const [label, url] = p.split(' LINK ');
      return { label: label.trim(), url: (url || '').trim() };
    }
    return { text: p };
  }).filter((p) => p.text || p.url);
}

/** “Quote text” - Source  ->  { quote, source } */
export function parseQuote(text) {
  const m = text.match(/^\s*["“”]([\s\S]+?)["“”]\s*[-–—]\s*([\s\S]+)$/);
  if (m) return { quote: m[1].trim(), source: m[2].trim() };
  const bare = text.match(/^\s*["“”]([\s\S]+?)["“”]\s*$/);
  if (bare) return { quote: bare[1].trim(), source: '' };
  return null;
}

export function toneOf(bg) {
  if (!bg) return null;
  const b = bg.toLowerCase();
  if (b === 'black') return 'dark';
  if (b === 'white') return 'light';
  const m = b.match(/^#?([0-9a-f]{6}|[0-9a-f]{3})$/);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const [r, g, bl] = [0, 2, 4].map((i) => parseInt(h.substr(i, 2), 16));
  return (r * 299 + g * 587 + bl * 114) / 1000 < 128 ? 'dark' : 'light';
}

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

const LEGACY_PATHS = {
  bio: '#about', calendar: '#live', recordings: '#music', donate: '#support', contact: '#contact',
  musicallianceproject: '#about',
};
const SECTION_IDS = new Set(['about', 'music', 'watch', 'live', 'band', 'support', 'contact']);

/** Understands full URLs, mailto:, old nav paths ("Bio", "/Contact") and #anchors. */
export function resolveLink(url) {
  const raw = clean(url);
  if (!raw) return { href: '', external: false, section: null };
  if (/^(mailto:|tel:)/i.test(raw)) return { href: raw, external: false, section: null };
  if (/^(https?:)?\/\//i.test(raw)) return { href: raw, external: true, section: null };
  if (/^www\./i.test(raw)) return { href: `https://${raw}`, external: true, section: null };
  if (raw.startsWith('#')) {
    const id = raw.slice(1);
    return { href: raw, external: false, section: SECTION_IDS.has(id) ? id : null };
  }
  const seg = raw.replace(/^\/+/, '').split('/')[0].toLowerCase();
  if (LEGACY_PATHS[seg]) return { href: LEGACY_PATHS[seg], external: false, section: LEGACY_PATHS[seg].slice(1) };
  return { href: `/${raw.replace(/^\/+/, '')}`, external: false, section: null };
}

// ---------------------------------------------------------------------------
// Rows -> blocks
// ---------------------------------------------------------------------------

function toBlock(r) {
  const base = { row: r.i, platform: r.platform, bg: r.bg };
  switch (r.type) {
    case 'title':
    case 'subtitle': {
      const text = r.content || (r.extra && r.extra.toLowerCase() !== 'nohr' ? r.extra : '');
      return text ? { ...base, kind: r.type, text } : null;
    }
    case 'text':
    case 'simpletext':
      return r.content ? { ...base, kind: 'text', text: r.content } : null;
    case 'link':
    case 'presslink':
      return r.content && r.extra ? { ...base, kind: 'link', label: r.content, url: r.extra } : null;
    case 'youtube':
      return r.content ? { ...base, kind: 'youtube', id: ytId(r.content) } : null;
    case 'image':
      return r.content ? { ...base, kind: 'image', src: r.content, maxWidth: num(r.extra) } : null;
    case 'imagelink':
      return r.content ? { ...base, kind: 'image', src: r.content, href: r.extra } : null;
    case 'imageintext': {
      const [, , href] = r.extra.split(',').map(clean);
      return r.content ? { ...base, kind: 'image', src: r.content, href } : null;
    }
    case 'images': {
      const srcs = r.content.split(',').map(clean).filter(Boolean);
      const [w, g] = r.extra.split(',');
      return srcs.length ? { ...base, kind: 'images', srcs, maxWidth: num(w), gap: num(g) } : null;
    }
    case 'imagearray': {
      const columns = r.content.split('$').map((col) => col.split(',').map(clean).filter(Boolean)).filter((c) => c.length);
      const [w, g] = r.extra.split(',');
      return columns.length ? { ...base, kind: 'imagearray', columns, maxWidth: num(w), gap: num(g) } : null;
    }
    case 'textwithimage': {
      const [image, href] = splitFirst(r.extra, ',').map(clean);
      return { ...base, kind: 'card', title: r.content, image, href, desc: r.desc };
    }
    case 'soundcloud':
      return r.content ? { ...base, kind: 'soundcloud', url: r.content } : null;
    case 'iframe': {
      if (!r.content) return null;
      const [w, h] = r.extra.split(',').map((s) => num(s));
      return { ...base, kind: 'iframe', src: r.content, width: w, height: h };
    }
    case 'hr':
      return { ...base, kind: 'hr' };
    case 'space':
      return { ...base, kind: 'space', size: num(r.content) || 0 };
    default:
      return null;
  }
}

/**
 * Splits a sheet into chapters. A new chapter starts at every Title row, and at
 * a Subtitle row whose background flips between light and dark.
 * Rows before the first Title form an untitled first chapter.
 */
export function chaptersOf(rows) {
  const out = [];
  let cur = { title: '', level: null, tone: null, blocks: [] };
  const flush = () => {
    const real = cur.blocks.filter((b) => b.kind !== 'space' && b.kind !== 'hr');
    if (cur.title || real.length) out.push({ ...cur, tone: cur.tone || 'light' });
  };

  (rows || []).forEach((row, i) => {
    const type = clean(row[3]).toLowerCase();
    if (!KNOWN_TYPES.has(type)) return;
    const r = {
      i, type, content: clean(row[0]), bg: clean(row[1]), extra: clean(row[2]),
      platform: clean(row[4]).toLowerCase(), desc: clean(row[5]),
    };
    const block = toBlock(r);
    if (!block) return;
    const tone = toneOf(r.bg);

    const startsChapter = block.kind === 'title' ||
      (block.kind === 'subtitle' && cur.tone && tone && tone !== cur.tone);
    if (startsChapter) {
      flush();
      cur = { title: block.text, level: block.kind, tone: tone || 'light', blocks: [] };
      return;
    }
    if (!cur.tone && tone && block.kind !== 'space') cur.tone = tone;
    cur.blocks.push(block);
  });
  flush();
  return out;
}

export const findChapter = (chapters, pattern) =>
  chapters.find((c) => pattern.test(c.title || '')) || null;
export const findChapters = (chapters, pattern) =>
  chapters.filter((c) => pattern.test(c.title || ''));
export const blocksOf = (chapter, kind) =>
  (chapter ? chapter.blocks : []).filter((b) => b.kind === kind);

/** Pulls "Piano" out of "Chase Morrin (Piano)" in any of the given texts. */
export function roleFor(name, texts) {
  const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  for (const t of texts) {
    const m = t.match(new RegExp(`${esc}\\s*\\(([^)]+)\\)`));
    if (m) return m[1].split(/ like |,| and /i)[0].trim();
  }
  return '';
}

// ---------------------------------------------------------------------------
// Recordings sheet (3 tables side by side)
// ---------------------------------------------------------------------------

export function parseRecordings(rows) {
  const g = (r, i) => clean((r || [])[i]);
  const videos = [], albums = [], singles = [];
  (rows || []).forEach((r) => {
    if (g(r, 0) && g(r, 1)) videos.push({ title: g(r, 0), id: ytId(g(r, 1)), desc: g(r, 2) });
    if (g(r, 5)) albums.push({ title: g(r, 5), image: g(r, 6), href: g(r, 7), desc: g(r, 8) });
    if (g(r, 9)) singles.push({ title: g(r, 9), image: g(r, 10), href: g(r, 11), desc: g(r, 12) });
  });
  return { videos, albums, singles };
}

// ---------------------------------------------------------------------------
// Home sheet news box (rows 1-8, label in column A, value in column B)
// ---------------------------------------------------------------------------

export function parseNewsBox(rows) {
  const val = (re) => {
    const row = (rows || []).slice(0, 10).find((r) => re.test(clean(r[0])));
    return row ? clean(row[1]) : '';
  };
  const news = {
    youtube: ytId(val(/youtube/i)),
    picture: val(/picture/i),
    linkText: val(/text below|link text/i),
    url: val(/to site/i),
    title: val(/title text/i),
  };
  return (news.youtube || news.picture) && (news.title || news.linkText) ? news : null;
}
